import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import webPushService from '../services/webPushService';

const AuthContext = createContext(null);

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token') || sessionStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Token whose user we've already loaded into state (via login/register/
  // loginWithToken). Lets the [token] effect skip a redundant /profile GET
  // when the session was just established with the user already in hand.
  const hydratedTokenRef = React.useRef(null);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/profile`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      if (hydratedTokenRef.current === token) {
        // Session just established with the user already set — skip refetch.
        setLoading(false);
        return;
      }
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  // Auto-subscribe to web push when user logs in (if they already granted permission)
  useEffect(() => {
    if (user && token && webPushService.isSupported()) {
      webPushService.init().then(() => {
        if (webPushService.getPermission() === 'granted' && !webPushService.isSubscribed()) {
          webPushService.subscribe().catch(() => {});
        }
      });
    }
  }, [user, token]);

  // Auto-detect and sync timezone (once per session)
  const timezoneSyncedRef = React.useRef(false);
  useEffect(() => {
    if (user && token && !timezoneSyncedRef.current) {
      timezoneSyncedRef.current = true;
      const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (detected && detected !== user.timezone) {
        axios.patch(`${API}/users/me/timezone`, { timezone: detected })
          .then(res => {
            if (res.data.status === 'ok') {
              setUser(prev => ({ ...prev, timezone: detected }));
            }
          })
          .catch(() => {});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, token]);

  // Sliding token renewal: re-issue a fresh 30-day token while the user is
  // active, so an active user is effectively never logged out. We update
  // localStorage + the axios header directly and intentionally do NOT call
  // setToken — the user is unchanged, and the request interceptor reads the
  // token from storage on every request, so storage is the source of truth.
  // Calling setToken would re-trigger the fetchUser effect on every refresh.
  const renewToken = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/auth/refresh`);
      const newToken = response.data?.token;
      if (newToken) {
        localStorage.setItem('token', newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      }
    } catch (e) {
      // A failed refresh is non-fatal: the existing token is valid until it
      // expires, and a 401 from a dead token is handled by the client interceptor.
    }
  }, []);

  useEffect(() => {
    if (!token) return undefined;
    renewToken(); // on app load
    const SIX_HOURS = 6 * 60 * 60 * 1000;
    const intervalId = setInterval(renewToken, SIX_HOURS);
    // Renew when the user returns to the tab, throttled to once per 5 min.
    let lastFocusRenew = 0;
    const onFocus = () => {
      const now = Date.now();
      if (now - lastFocusRenew > 5 * 60 * 1000) {
        lastFocusRenew = now;
        renewToken();
      }
    };
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
    };
  }, [token, renewToken]);

  const refreshUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/profile`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  }, []);

  // Every login persists on the device (localStorage) so the user stays logged
  // in across browser restarts. The backend issues a 30-day token which the
  // sliding-renewal effect below keeps rolling forward while the user is active.
  // The rememberMe arg is kept for call-site compat but no longer changes storage.
  const login = useCallback(async (email, password, rememberMe = true) => {
    const response = await axios.post(`${API}/auth/login`, { email, password, remember_me: true });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    sessionStorage.removeItem('token');
    localStorage.removeItem('rememberMe');
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    hydratedTokenRef.current = newToken;
    setToken(newToken);
    setUser(userData);
    return userData;
  }, []);

  // userData is an optional pre-fetched user (e.g. the OAuth callback already
  // returns it) — when provided we skip the /profile round-trip entirely.
  const loginWithToken = useCallback(async (newToken, userData = null) => {
    localStorage.setItem('token', newToken);
    sessionStorage.removeItem('token');
    localStorage.removeItem('rememberMe');
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    // Mark hydrated before the effect runs so it doesn't also fetch /profile.
    hydratedTokenRef.current = newToken;
    setToken(newToken);
    if (userData && userData.id) {
      setUser(userData);
      return userData;
    }
    const response = await axios.get(`${API}/profile`);
    setUser(response.data);
    return response.data;
  }, []);

  const register = useCallback(async (email, password, name, inviteCode = null) => {
    const payload = { email, password, name };
    if (inviteCode) {
      payload.invite_token = inviteCode;
    }
    const response = await axios.post(`${API}/auth/register`, payload);
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    hydratedTokenRef.current = newToken;
    setToken(newToken);
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('rememberMe');
    sessionStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    hydratedTokenRef.current = null;
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    user,
    token,
    loading,
    login,
    loginWithToken,
    register,
    logout,
    refreshUser,
    isAdmin: user?.role === 'admin',
    isCoach: user?.role === 'coach' || user?.role === 'admin',
    isClient: user?.role === 'client',
  }), [user, token, loading, login, loginWithToken, register, logout, refreshUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
